import { useMutation, useQuery } from "@tanstack/react-query"
import {
  ClientOnly,
  createFileRoute,
  useStableCallback,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  type ColumnDef,
  createColumnHelper,
  type VisibilityState,
} from "@tanstack/react-table"
import { PencilIcon, PlusIcon, PowerIcon } from "lucide-react"
import { useId, useMemo, useState } from "react"

import {
  ActionsDropdownMenu,
  type ActionsDropdownMenuGroup,
} from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { AddLocaleModal } from "~/modules/system-config/components/add-locale-modal"
import { EditLocaleModal } from "~/modules/system-config/components/edit-locale-modal"
import { updateLocaleStatusFn } from "~/modules/system-config/system-config.functions"
import { getAvailableLocalesQueryOptions } from "~/modules/system-config/system-config.queries"
import type { Locale } from "~/modules/system-config/system-config.service"
import { ComponentErrorBoundary } from "~/root-components/component-error-boundary"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/_admin-layout/system-config/locales",
)({
  component: SystemConfigLocales,
})

const columnHelper = createColumnHelper<Locale>()
const TABLE_VISIBLE_COLUMNS_KEY = "system-config-locales-visible-columns"

function SystemConfigLocales() {
  const toastId = useId()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedLocale, setSelectedLocale] = useState<Locale | null>(null)

  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const { data: locales = [], isLoading } = useQuery(
    getAvailableLocalesQueryOptions(["active", "inactive"]),
  )

  const updateLocaleStatus = useServerFn(updateLocaleStatusFn)
  const updateStatusMutation = useMutation({
    meta: {
      invalidates: [getAvailableLocalesQueryOptions().queryKey],
    },
    mutationFn: updateLocaleStatus,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Locale status updated.", { id: toastId })
      } else {
        toast.error(result.message, { id: toastId })
      }
    },
    onError: (error) => {
      toast.error(error.message, { id: toastId })
    },
  })

  const getActionsGroups = useStableCallback(
    (locale: Locale): ActionsDropdownMenuGroup[] => [
      {
        actions: [
          {
            label: "Edit Locale",
            icon: PencilIcon,
            onClick: () => {
              setSelectedLocale(locale)
              setShowEditModal(true)
            },
          },
        ],
      },
      {
        actions: [
          {
            label:
              locale.status === "active" ? "Disable Locale" : "Enable Locale",
            icon: PowerIcon,
            onClick: () => {
              toast.loading("Updating status...", { id: toastId })
              updateStatusMutation.mutate({
                data: {
                  code: locale.code,
                  status: locale.status === "active" ? "inactive" : "active",
                },
              })
            },
            variant:
              locale.status === "active"
                ? ("destructive" as const)
                : ("success" as const),
          },
        ],
      },
    ],
  )

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<Locale, any>[] = useMemo(
    () => [
      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="min-w-[120px]">Code</div>,
        cell: (info) => (
          <span className="font-mono text-white">{info.getValue()}</span>
        ),
        meta: { columnName: "Code" },
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="min-w-[200px]">Name</div>,
        cell: (info) => <span className="text-white">{info.getValue()}</span>,
        meta: { columnName: "Name" },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: (info) => (
          <Badge
            variant={info.getValue() === "active" ? "default" : "secondary"}
            className={
              info.getValue() === "active"
                ? "bg-green-500/20 text-green-400"
                : "bg-gray-500/20 text-gray-400"
            }
          >
            {info.getValue() === "active" ? "Active" : "Disabled"}
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            { label: "Active", value: "active" },
            { label: "Disabled", value: "inactive" },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="grow text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <ActionsDropdownMenu groups={getActionsGroups(row.original)} />
          </div>
        ),
      }),
    ],
    [getActionsGroups],
  )

  return (
    <PageDetailsPanel
      title="Locales"
      actions={
        <Button onClick={() => setShowAddModal(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Locale
        </Button>
      }
    >
      <div className="flex-1 overflow-auto p-3">
        <DataTable
          data={locales}
          columns={columns}
          searchPlaceholder="Search locales..."
          noResultsMessage={isLoading ? "Loading..." : "No locales found"}
          manualPagination={false}
          manualSorting={false}
          manualFiltering={false}
          getInitialColumnVisibility={() => visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
        />
      </div>

      <ClientOnly>
        <ComponentErrorBoundary>
          <AddLocaleModal open={showAddModal} onOpenChange={setShowAddModal} />

          {selectedLocale && (
            <EditLocaleModal
              key={selectedLocale.code}
              open={showEditModal}
              onOpenChange={setShowEditModal}
              locale={selectedLocale}
            />
          )}
        </ComponentErrorBoundary>
      </ClientOnly>
    </PageDetailsPanel>
  )
}
